import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ChatbotService, ChatStreamEvent, BotStatus } from './feature-services/chatbot.service';
import { ToasterService } from './toaster.service';
import { clampDockWidth, defaultDockWidth, DOCK_SSR_FALLBACK_WIDTH } from '../utils/dock-resize.util';

export interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  isStreaming?: boolean;
  isError?: boolean;
  feedback?: 'up' | 'down' | null;

  turn_id?: string | null;
  feedbackSubmitted?: boolean;
  feedbackPending?: boolean;
  redirect_url?: string | null;
  isSystemMessage?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ChatStateService {

  private readonly _isDockOpen = new BehaviorSubject(false);
  readonly isDockOpen$ = this._isDockOpen.asObservable();

  private readonly _dockWidth = new BehaviorSubject<number>(
    typeof window !== 'undefined'
      ? defaultDockWidth(window.innerWidth)
      : DOCK_SSR_FALLBACK_WIDTH,
  );
  readonly dockWidth$ = this._dockWidth.asObservable();


  private readonly _messages = new BehaviorSubject<ChatMessage[]>([]);
  readonly messages$ = this._messages.asObservable();

  private readonly _isLoading = new BehaviorSubject(false);
  readonly isLoading$ = this._isLoading.asObservable();

  private _abortController: AbortController | null = null;
  private _streamSub: Subscription | null = null;

  constructor(
    private readonly chatbotService: ChatbotService,
    private readonly toaster: ToasterService,
  ) { }


  get isDockOpen(): boolean {
    return this._isDockOpen.value;
  }

  get dockWidth(): number {
    return this._dockWidth.value;
  }

  get messages(): ChatMessage[] {
    return this._messages.value;
  }

  get isLoading(): boolean {
    return this._isLoading.value;
  }

  get botStatus$(): import('rxjs').Observable<BotStatus | null> {
    return this.chatbotService.botStatus$;
  }

  startStatusPolling(): void {
    this.chatbotService.startStatusPolling();
  }

  stopStatusPolling(): void {
    this.chatbotService.stopStatusPolling();
  }


  openDock(initialQuery?: string): void {
    this._dockWidth.next(defaultDockWidth(window.innerWidth));

    this._isDockOpen.next(true);

    if (initialQuery?.trim()) {
      this.sendMessage(initialQuery.trim());
    }
  }

  closeDock(): void {
    this.stopGenerating();
    this._isDockOpen.next(false);
  }

  setDockWidth(width: number): void {
    this._dockWidth.next(clampDockWidth(window.innerWidth, width));
  }


  clearMessages(): void {
    this.stopGenerating();
    this._messages.next([]);
    this.chatbotService.clearSession();
  }

  stopGenerating(): void {
    this._abortController?.abort();
    this._abortController = null;

    this._streamSub?.unsubscribe();
    this._streamSub = null;

    this.updateMessages(messages =>
      messages.map(message =>
        message.isStreaming
          ? {
            ...message,
            isStreaming: false,
          }
          : message,
      ),
    );

    this._isLoading.next(false);
  }

  sendMessage(text: string): void {
    this.updateMessages(messages => [
      ...messages,
      this.createUserMessage(text),
    ]);

    this.streamMessage(text);
  }

  /**
   * Re-sends the user message that immediately preceded the given bot message,
   * discarding that bot reply and everything after it.
   */
  retryFrom(botMessageId: string): void {
    const messages = this._messages.value;
    const botIndex = messages.findIndex(m => m.id === botMessageId);
    if (botIndex === -1) return;

    // Find the user message immediately preceding this specific bot message
    let userIndex = -1;
    for (let i = botIndex - 1; i >= 0; i--) {
      if (messages[i].sender === 'user') {
        userIndex = i;
        break;
      }
    }
    if (userIndex === -1) return;

    const userMessage = messages[userIndex];
    // Keep messages up to and including the preceding user message
    const trimmedMessages = messages.slice(0, userIndex + 1);

    this._messages.next(trimmedMessages);
    this.stopGenerating();
    this.streamMessage(userMessage.text);
  }

  submitFeedback(messageId: string, rating: 'up' | 'down', comment?: string | null): void {
    const message = this._messages.value.find(m => m.id === messageId);
    if (!message) return;

    if (message.turn_id === undefined) return;

    const previousFeedback = message.feedback ?? null;
    const newFeedback = previousFeedback === rating ? null : rating;

    this.updateMessage(messageId, { feedback: newFeedback });

    if (!message.turn_id) {
      return;
    }

    if (newFeedback === null) return;

    // Disable buttons immediately while the request is in-flight
    this.updateMessage(messageId, { feedbackPending: true });

    this.chatbotService
      .submitFeedback(message.turn_id, newFeedback, comment ?? null)
      .then(() => {
        this.updateMessage(messageId, { feedbackSubmitted: true, feedbackPending: false });
      })
      .catch((err: unknown) => {
        const status = (err as any)?.status;
        if (status === 503) {
          this.updateMessage(messageId, { feedback: previousFeedback, turn_id: null, feedbackPending: false });
        } else {
          this.updateMessage(messageId, { feedback: previousFeedback, feedbackPending: false });
          this.toaster.error('Could not save feedback. Please try again.');
        }
        console.warn('Chatbot feedback error:', err);
      });
  }

  private updateMessages(
    updater: (messages: ChatMessage[]) => ChatMessage[],
  ): void {
    this._messages.next(updater(this._messages.value));
  }

  private updateMessage(id: string, changes: Partial<ChatMessage>): void {
    this.updateMessages(messages =>
      messages.map(message =>
        message.id === id ? { ...message, ...changes } : message,
      ),
    );
  }

  private appendToken(id: string, token: string): void {
    this.updateMessages(messages =>
      messages.map(message =>
        message.id === id
          ? { ...message, text: message.text + token }
          : message,
      ),
    );
  }

  private createUserMessage(text: string): ChatMessage {
    return {
      id: `user_${Date.now()}`,
      text,
      sender: 'user',
      timestamp: new Date(),
    };
  }

  private createBotMessage(id: string): ChatMessage {
    return {
      id,
      text: '',
      sender: 'bot',
      timestamp: new Date(),
      isStreaming: true,
      isError: false,
      feedback: null,
      turn_id: undefined, // unknown until done event
      feedbackSubmitted: false,
    };
  }

  private resetStreaming(): void {
    this._isLoading.next(false);
    this._abortController = null;
    this._streamSub = null;
  }

  private streamMessage(text: string): void {
    if (this._abortController || this._streamSub) {
      this.stopGenerating();
    }

    this._isLoading.next(true);

    const botId = `bot_${Date.now()}`;

    this.updateMessages(messages => [
      ...messages,
      this.createBotMessage(botId),
    ]);

    this._abortController = new AbortController();

    this._streamSub = this.chatbotService
      .chatWithBot(text, this._abortController.signal, this.chatbotService.sessionId$.value)
      .subscribe({
        next: (event: ChatStreamEvent) => {
          if (event.kind === 'token') {
            this.appendToken(botId, event.content);
          } else if (event.kind === 'ticket') {
            // Replace any streamed text with the ticket's formatted message
            // and store the redirect URL for in-app navigation
            this.updateMessage(botId, {
              text: event.message,
              redirect_url: event.redirect_url,
            });
          } else if (event.kind === 'done') {
            // Store turn_id on the message; null means logging is off
            this.updateMessage(botId, { turn_id: event.turn_id });
          }
        },

        error: (_err: unknown) => {
          this.toaster.error(
            'Chatbot is unavailable. Please try again later.',
          );

          this.updateMessage(botId, {
            text: 'Sorry, something went wrong. Please try again.',
            isStreaming: false,
            isError: true,
          });

          this.resetStreaming();
        },

        complete: () => {
          const finalText = this._messages.value.find(m => m.id === botId)?.text ?? '';
          // Mark session-timeout and similar system injections so the action row is suppressed
          const isSystemMessage = /timed out|session ended|inactivity/i.test(finalText);
          this.updateMessage(botId, { isStreaming: false, isError: false, isSystemMessage });
          this.resetStreaming();
        },
      });
  }
}
